import {Component, Input} from '@angular/core';
import {StepSelectFileOutput} from '../../models/step-select-file-output';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {StepperService} from '../../../stepper/stepper.service';
import {StepComponent, StepData} from '../../../stepper/step/step.component';
import {Step} from '../../../stepper/step/step';
import {MydocmaPrompt, PromptDialogComponent} from '@mydocma/component-library/src/lib/prompt-dialog';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';

interface SheetRowItemConfig {
    columnName: string;
    foundedValue: string;
    label: string;
}

@Component({
    selector: 'mydocma-import-step-file-data-mapping',
    templateUrl: './step-file-data-mapping.component.html',
    styleUrls: ['./step-file-data-mapping.component.scss']
})
export class StepFileDataMappingComponent implements StepComponent {

    @Input()
    public step: Step;

    @Input()
    public fieldsToImportConfig;

    public sheetData: StepSelectFileOutput;

    public sheetItems: SheetRowItemConfig[] = [];

    public formGroup = new FormGroup({});

    public hide = false;

    private data;

    constructor(
        private readonly stepperService: StepperService,
        private readonly matDialog: MatDialog,
        private readonly translateService: TranslateService
    ) {
    }

    public enter() {
        if (this.step.completed) {
            return;
        }

        const stepSelectFile = this.stepperService.getStepByName('selectFile');
        this.sheetData = stepSelectFile.data;

        this.formGroup = new FormGroup({});
        this.sheetItems = [];

        this.fieldsToImportConfig.fields.forEach((field) => {
            const foundedValue = this.sheetData.data[0].find(value => value.toLowerCase() === field.name.toLowerCase());

            const validators = [];
            if (field.required) {
                validators.push(Validators.required);
            }

            this.formGroup.addControl(field.name, new FormControl(foundedValue, validators));
            this.formGroup.controls[field.name].setValue(foundedValue);

            this.sheetItems.push({
                columnName: field.name,
                foundedValue,
                label: field.label
            });
        });

        this.setData();
    }

    public async validate(): Promise<StepData> {
        if (!this.formGroup.valid) {
            await this.promptDiscardChanges();
        } else {
            return {
                error: null,
                data: this.data,
                valid: true
            };
        }
    }

    public onFieldSelectionChange() {
        this.setData();
    }

    public setData() {
        const formValue = this.formGroup.value;
        if (Object.values(formValue).every(item => item === false)) {
            return;
        }

        for (const [key, value] of Object.entries(formValue)) {
            if (!value) {
                delete formValue[key];
            }
        }

        this.data = formValue;
        this.stepperService.setStepData(this.step, {
            error: null,
            data: this.data,
            valid: true
        });
    }

    public async previousStep() {
        await this.stepperService.prevStep();
    }

    public async nextStep() {
        if (this.formGroup.valid) {
            await this.stepperService.nextStep();
            this.setData();

            return;
        }

        await this.promptDiscardChanges();
    }

    public onCheckboxChange() {
        this.hide = !this.hide;
    }

    public isFieldMapped(value) {
        return !(this.formGroup.get(value).value && this.hide);
    }

    private async promptDiscardChanges(): Promise<boolean> {
        const dialogData: MydocmaPrompt = {
            title: await this.translateService.get('IE.step-file-data-mapping.dialog-mapping.errorTiTle').toPromise(),
            content: await this.translateService.get('IE.step-file-data-mapping.dialog-mapping.errorText').toPromise(),
            yes: await this.translateService.get('IE.step-file-data-mapping.dialog-mapping.close').toPromise(),
            no: null,
        };
        const dialogRef = this.matDialog.open(PromptDialogComponent, {data: dialogData});

        return await dialogRef.afterClosed().toPromise();
    }
}
